import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";

// Universe lore modal — Founder coffin nail 2026-07-02+:
// "Like a treatment per universe at a click on the banner? That gives a
//  lore-only overview? That's a COFFIN NAIL. Great work. Do it."
//
// Fetches public treatment from GET /api/worlds/{slug}/lore. Renders a
// full-screen modal with the world's hook, canonical lore body, tone words,
// palette + a "Visit the world →" outbound CTA to the domain.

export default function WorldLoreModal({ slug, name, domain, onClose }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setPack(null);
    setErr(false);
    let cancelled = false;
    // Founder plain-ink 2026-02-15: treatments were stuck on the loading
    // screen. Guard against unmount + surface fetch/parse failures so we
    // never leave the modal in a phantom-loading state.
    fetch(`${backend}/api/worlds/${slug}/lore`)
      .then((r) => {
        if (!r.ok) return Promise.reject(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        if (!d || !d.title) return Promise.reject("empty payload");
        setPack(d);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[world-lore] fetch failed for", slug, e);
        setErr(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, backend]);

  const accent = pack?.accent || "#ff5a00";
  const displayFont = pack?.typography?.family || "'Cormorant Garamond', serif";

  return (
    <AnimatePresence>
      <motion.div
        key="world-lore-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="world-lore-backdrop"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/92 backdrop-blur-md px-4 py-10 overflow-y-auto"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          data-testid={`world-lore-modal-${slug}`}
          className="relative w-full max-w-3xl bg-[#0b0b0d] border border-white/10 my-auto"
          style={{ borderColor: `${accent}55` }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close lore treatment"
            data-testid="world-lore-close"
            className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center text-[#a1a1aa] hover:text-white border border-white/15 hover:border-white/30 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {pack && (
            <>
              {/* Hero image band */}
              {pack.art?.still && (
                <div
                  className="relative w-full h-[220px] sm:h-[300px] bg-center bg-cover"
                  style={{ backgroundImage: `url(${pack.art.still})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0b0b0d]" />
                </div>
              )}

              <div className="px-6 sm:px-10 pt-8 pb-10 space-y-6">
                <div>
                  <p
                    className="font-mono text-[10px] uppercase tracking-[0.42em] mb-3"
                    style={{ color: accent }}
                  >
                    {pack.tag}
                    {pack.mature && (
                      <span className="ml-3 text-[9px] font-bold text-[#C0392B] border border-[#C0392B]/60 rounded px-1.5 py-0.5 bg-[#C0392B]/10">
                        18+
                      </span>
                    )}
                  </p>
                  <h2
                    className="uppercase text-[#f5f0e8] leading-[0.95]"
                    style={{
                      fontFamily: displayFont,
                      fontSize: "clamp(2rem, 5vw, 3.5rem)",
                      letterSpacing: "0.015em",
                    }}
                    data-testid="world-lore-title"
                  >
                    {pack.title}
                  </h2>
                </div>

                <p
                  data-testid="world-lore-hook"
                  className="font-display italic text-lg sm:text-xl text-[#d4d4d8] leading-relaxed border-l-2 pl-4"
                  style={{ borderColor: accent }}
                >
                  {pack.hook}
                </p>

                <div
                  data-testid="world-lore-body"
                  className="font-body text-[15px] sm:text-base text-[#d4d4d8] leading-[1.75] space-y-4 whitespace-pre-line"
                >
                  {pack.lore}
                </div>

                {/* Tone words + palette */}
                <div className="pt-4 border-t border-white/10 grid gap-6 sm:grid-cols-2">
                  {pack.tone_words?.length > 0 && (
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a] mb-3">
                        Tone
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {pack.tone_words.map((w) => (
                          <span
                            key={w}
                            className="font-mono text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 border border-white/10 text-[#a1a1aa]"
                          >
                            {w}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {pack.art?.palette?.length > 0 && (
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a] mb-3">
                        Palette
                      </p>
                      <div className="flex gap-2">
                        {pack.art.palette.map((c) => (
                          <span
                            key={c}
                            title={c}
                            className="w-8 h-8 border border-white/20"
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {pack.domain && (
                  <div className="pt-6 border-t border-white/10">
                    <a
                      href={pack.domain}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="world-lore-visit"
                      className="inline-flex items-center gap-2 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.32em] transition-colors"
                      style={{
                        color: "#050505",
                        backgroundColor: accent,
                      }}
                    >
                      Visit the world <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            </>
          )}

          {!pack && !err && (
            <div className="p-16 text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a]">
                Loading treatment…
              </p>
            </div>
          )}

          {err && (
            <div className="p-16 text-center space-y-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
                No treatment on the wire yet
              </p>
              <p className="font-body text-sm text-[#a1a1aa]">
                {name} is queued for its own domain reveal.
              </p>
              {domain && (
                <a
                  href={domain}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ffbb00]"
                >
                  Visit the domain anyway →
                </a>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
